import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, makeEnvironmentProviders, inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { common, lightThemePalette, darkThemePalette } from '@ux-foundry/palette';
import color from 'color';

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
const { black, white } = common;
const fabLightThemePalette = {
    ...lightThemePalette,
    black,
    white,
    accent: lightThemePalette.secondary,
    contrast: {
        ...lightThemePalette.contrast,
        black: white,
        white: black,
        accent: lightThemePalette.contrast.secondary,
    },
};
const fabDarkThemePalette = {
    ...darkThemePalette,
    black,
    white,
    accent: darkThemePalette.secondary,
    contrast: {
        ...darkThemePalette.contrast,
        black: white,
        white: black,
        accent: darkThemePalette.contrast.secondary,
    },
};
fabDarkThemePalette.surface = color(white).mix(color(fabDarkThemePalette.background), 0.92).hex();

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Injection Token for providing the theme setting.
 */
const FAB_THEME_SETTING = new InjectionToken('fab.theme.setting');
/**
 * This function provides the theme setting to standalone applications
 * @param themeSetting Theme setting you'd like to apply globally
 * @returns Environment provider for the theme setting injection token
 */
function provideThemeSetting(themeSetting) {
    return makeEnvironmentProviders([{ provide: FAB_THEME_SETTING, useValue: themeSetting }]);
}

/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class FabThemeService {
    get activeThemePalette() {
        return this.activeTheme === 'dark' ? this.darkThemePalette : this.lightThemePalette;
    }
    get activeTheme() {
        return this._activeTheme;
    }
    /**
     * CSS class used to apply to html elements to achieve the currently active theme
     * @returns the appropriate fab theme css class, depending on what them is currently active
     * (`fab-light-theme` or `fab-dark-theme`)
     */
    get themeCssClass() {
        return `fab-${this.activeTheme}-theme`;
    }
    get themeSetting() {
        return this._themeSetting;
    }
    set themeSetting(themeSetting) {
        let newTheme;
        // If themeSetting is 'system', check if system themes are supported.  If not, reset to light theme
        // If they are, get the system theme.
        if (themeSetting === 'system') {
            if (this.isSystemThemeSupported) {
                newTheme = this.getSystemTheme();
            }
            else {
                console.error(`System themes are not supported by this browser.  Leaving themeSetting as '${this._themeSetting}'.`);
                return;
            }
        }
        else {
            newTheme = themeSetting;
        }
        this.updateTheme(newTheme);
        this._themeSetting = themeSetting;
    }
    /**
     * Tells if system theme is supported
     * @returns true if system theme is supported
     */
    get isSystemThemeSupported() {
        return !!window.matchMedia;
    }
    /**
     * Creates a new instance of the service and uses the FAB_THEME_SETTING injection token to apply the themeSetting,
     * if provided.  If not, it defaults to 'system'.
     */
    constructor() {
        /**
         * Dark and Light theme palette objects
         */
        this.lightThemePalette = fabLightThemePalette;
        this.darkThemePalette = fabDarkThemePalette;
        this._themeChange = new BehaviorSubject(this.activeTheme);
        /**
         * The currently active theme.  This is based on themeSetting and derived from the
         * system's setting if themeSetting is 'system'
         */
        this._activeTheme = 'light';
        /**
         * The setting the user would like for their theme.  If system, the theme will be derived
         * from the user's system setting.  Default to system if supported.
         */
        this._themeSetting = 'light';
        /** DI injected services */
        this.document = inject(DOCUMENT);
        this.document.body.classList.add(this.themeCssClass);
        const themeSetting = inject(FAB_THEME_SETTING, { optional: true });
        this.themeSetting = themeSetting ?? this.themeSetting;
        this.createSystemThemeChangeListener();
        this.themeChange = this._themeChange.asObservable();
    }
    /**
     * Add event listener to the window and listen for changes in theme.
     */
    createSystemThemeChangeListener() {
        if (this.isSystemThemeSupported) {
            const systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
            systemThemeQuery.addEventListener('change', (e) => {
                if (this.themeSetting === 'system') {
                    const systemTheme = e.matches ? 'dark' : 'light';
                    this.updateTheme(systemTheme);
                }
            });
        }
    }
    /**
     * Retrieve the user's system theme.
     * @returns the user's system theme
     */
    getSystemTheme() {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        else {
            return 'light';
        }
    }
    /**
     * Update the theme to a new one.
     * @param newTheme new theme to apply
     */
    updateTheme(newTheme) {
        if (newTheme !== this.activeTheme) {
            const activeClass = this.themeCssClass;
            this._activeTheme = newTheme;
            // If the body already has the current class, replace it with the new one.  If not, just add the new one.
            if (this.document.body.classList.contains(activeClass)) {
                this.document.body.classList.replace(activeClass, this.themeCssClass);
            }
            else {
                this.document.body.classList.add(this.themeCssClass);
            }
        }
        // Observer for the theme changes. Subscribe to the `themeChange` property to be notified for every theme change event.
        this._themeChange.next(newTheme);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabThemeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabThemeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FAB_THEME_SETTING, FabThemeService, provideThemeSetting };
